import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { Home, Shield, Loader2, Check } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Post-signup role picker.
 * Shown once, after POPIA consent and before /find-building, for any user whose
 * role_selected flag is false. Self-service is restricted to Resident / Trustee.
 * Managing Agent / Building Owner / Developer arrive via invite/verification only.
 */
export default function SelectRole() {
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const [primary, setPrimary] = useState('resident');
  const [alsoTrustee, setAlsoTrustee] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    setSubmitting(true);
    try {
      const token = localStorage.getItem('session_token');
      const res = await fetch(`${API_URL}/api/auth/role/select`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: 'include',
        body: JSON.stringify({ primary_role: primary, also_trustee: alsoTrustee }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || 'Could not save your role');
      setUser((prev) => ({ ...(prev || {}), ...data }));
      toast.success('Role saved');
      navigate('/find-building', { replace: true });
    } catch (err) {
      toast.error(err?.message || 'Could not save your role');
    } finally {
      setSubmitting(false);
    }
  };

  const RoleCard = ({ value, icon: Icon, title, subtitle, testId }) => {
    const selected = primary === value;
    return (
      <button
        type="button"
        onClick={() => { setPrimary(value); if (value === 'trustee') setAlsoTrustee(false); }}
        data-testid={testId}
        className={`w-full text-left rounded-xl border-2 p-5 transition-all ${
          selected
            ? 'border-sky-500 bg-sky-50 ring-4 ring-sky-100'
            : 'border-slate-200 hover:border-slate-300 bg-white'
        }`}
      >
        <div className="flex items-start gap-4">
          <div className={`w-11 h-11 rounded-lg flex items-center justify-center ${selected ? 'bg-sky-500 text-white' : 'bg-slate-100 text-slate-600'}`}>
            <Icon className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-slate-900 flex items-center gap-2">
              {title}
              {selected && <Check className="w-4 h-4 text-sky-600" />}
            </div>
            <div className="text-sm text-slate-600 mt-0.5">{subtitle}</div>
          </div>
        </div>
      </button>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4 py-10">
      <Card className="max-w-xl w-full" data-testid="select-role-card">
        <CardHeader>
          <CardTitle>Welcome{user?.name ? `, ${user.name}` : ''} 👋</CardTitle>
          <CardDescription>
            Tell us who you are so we can set up the right experience for you.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <RoleCard
            value="resident"
            icon={Home}
            title="Resident"
            subtitle="I live in or own a unit and want to stay connected with my community."
            testId="role-option-resident"
          />
          <RoleCard
            value="trustee"
            icon={Shield}
            title="Trustee / Resident"
            subtitle="I serve on the board of trustees and also live in the building."
            testId="role-option-trustee"
          />

          {primary === 'resident' && (
            <div className="flex items-start gap-3 p-4 rounded-lg border border-slate-200 bg-slate-50">
              <Checkbox
                id="also-trustee"
                checked={alsoTrustee}
                onCheckedChange={setAlsoTrustee}
                data-testid="also-trustee-checkbox"
                className="mt-1"
              />
              <Label htmlFor="also-trustee" className="text-sm leading-relaxed text-slate-800 cursor-pointer">
                <strong>I am also a Trustee</strong> for my building.
                <span className="block text-xs text-slate-500 mt-1">
                  You&apos;ll get a switcher in the header to toggle between Resident and Trustee views.
                </span>
              </Label>
            </div>
          )}

          <div className="pt-2 text-xs text-slate-500 leading-relaxed">
            Managing Agents, Building Owners and Developers use dedicated invitation flows.
            If you were expecting one of those roles, please contact your administrator.
          </div>

          <Button
            onClick={submit}
            disabled={submitting}
            className="w-full"
            data-testid="submit-role-btn"
          >
            {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Continue
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
